import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BusinessUnitTreeNode } from
  '@app/api/user-service/model/businessUnitTreeNode';
import { BusinessUnitTreeComponent } from
  './components/business-unit-tree/business-unit-tree.component';
import { BusinessUnitManagementComponent } from
  './components/business-unit-management/business-unit-management.component';
import { BusinessUnitManagementService } from
  './services/business-unit-management.service';
import { BusinessUnitService as ApiBusinessUnitService } from
  '@app/api/user-service/api/businessUnit.service';
import { UnlinkBusinessUnitRequest } from
  '@app/api/user-service/model/unlinkBusinessUnitRequest';
import { PermissionService } from '@app/core/services/permission.service';
import { AssignStoreDialogComponent } from
  './components/assign-store-dialog/assign-store-dialog.component';
import { AssignUserDialogComponent } from
  './components/assign-user-dialog/assign-user-dialog.component';

@Component({
  selector: 'app-business-unit',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    ConfirmDialogModule,
    BusinessUnitTreeComponent,
    BusinessUnitManagementComponent,
  ],
  providers: [DialogService, MessageService, ConfirmationService],
  templateUrl: './business-unit.component.html',
  styleUrl: './business-unit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessUnitComponent implements OnInit {
  readonly businessUnits = signal<BusinessUnitTreeNode[]>([]);
  readonly users = signal<Array<Record<string, unknown>>>([]);
  readonly selectedUnitId = signal<string | null>(null);
  readonly selectedUnitType = signal<string | null>(null);
  readonly userSearchForReload = signal('');
  readonly expandedUnits = signal<Set<string>>(new Set<string>());
  readonly treeLoading = signal(false);
  readonly usersLoading = signal(false);

  readonly selectedUnitName = computed(() =>
    this.findUnitName(this.businessUnits(), this.selectedUnitId()),
  );

  readonly canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.business_unit.edit');
  });

  readonly canAssignUsers = computed(() =>
    this.selectedUnitType() === 'STORE',
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly businessUnitService = inject(BusinessUnitManagementService);
  private readonly apiBusinessUnitService = inject(ApiBusinessUnitService);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private dialogRef: DynamicDialogRef | null = null;

  ngOnInit(): void {
    this.subscribeToTreeState();
    this.loadBusinessUnits();
  }

  onUnitSelect(unit: BusinessUnitTreeNode): void {
    const unitId = this.getUnitKey(unit);

    if (!unitId) {
      return;
    }

    this.selectedUnitId.set(unitId);
    this.selectedUnitType.set(unit.type ?? null);
    this.userSearchForReload.set('');
    this.loadUsers(unitId, '');
  }

  onExpandedUnitsChange(expandedUnits: Set<string>): void {
    this.expandedUnits.set(new Set(expandedUnits));
  }

  onUserUnassigned(): void {
    const unitId = this.selectedUnitId();
    const search = this.userSearchForReload();
    if (unitId) {
      this.loadUsers(unitId, search);
    }
  }

  onUserSearchChange(search: string): void {
    const unitId = this.selectedUnitId();
    this.userSearchForReload.set(search);
    if (unitId) {
      this.loadUsers(unitId, search);
    }
  }

  onAssignStore(): void {
    const header = this.translateService.instant(
      'configuration.business_unit.dialog.assign_store_title',
    );

    this.dialogRef = this.dialogService.open(AssignStoreDialogComponent, {
      header,
      width: '600px',
      modal: true,
      closable: true,
      dismissableMask: true,
      closeOnEscape: true,
      focusOnShow: true,
      styleClass: 'assign-store-dialog-container',
    });

    this.dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.success) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'configuration.business_unit.dialog.assign_store_success',
            ),
          });
          this.loadBusinessUnits();
        }
        this.dialogRef = null;
      });
  }

  onAssignUser(): void {
    const unitId = this.selectedUnitId();
    if (!unitId) {
      return;
    }

    const businessUnitId = Number.parseInt(unitId, 10);
    if (Number.isNaN(businessUnitId)) {
      return;
    }

    const existingIds = new Set<string>();
    const existingEmails = new Set<string>();
    for (const u of this.users()) {
      const orig = u['_original'] as { id?: string; email?: string } | undefined;
      if (orig?.id) existingIds.add(orig.id);
      const email = orig?.email;
      if (email && typeof email === 'string') {
        existingEmails.add(email.trim());
      }
    }

    const header = this.translateService.instant(
      'configuration.business_unit.dialog.assign_user_title',
    );

    this.dialogRef = this.dialogService.open(AssignUserDialogComponent, {
      header,
      width: '560px',
      modal: true,
      closable: true,
      dismissableMask: true,
      closeOnEscape: true,
      focusOnShow: true,
      data: {
        businessUnitId,
        existingUserIds: Array.from(existingIds),
        existingUserEmails: Array.from(existingEmails),
        onAssignSuccess: () => this.loadUsers(unitId, this.userSearchForReload()),
      },
      styleClass: 'assign-user-dialog-container',
    });

    this.dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.success) {
          this.loadUsers(unitId, this.userSearchForReload());
        }
        this.dialogRef = null;
      });
  }

  onUnlinkChannel(unit: BusinessUnitTreeNode): void {
    if (unit?.id == null) {
      return;
    }

    const name = unit.name || unit.code || String(unit.id);
    const message = this.translateService.instant(
      'configuration.business_unit.dialog.unlink_channel_confirm',
      { name },
    );

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant(
        'configuration.business_unit.dialog.unlink_channel_confirm_title',
      ), 
      acceptLabel: this.translateService.instant('common.actions.confirm'),
      rejectLabel: this.translateService.instant('common.actions.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      blockScroll: true,
      closeOnEscape: true,
      dismissableMask: true,
      accept: () => this.executeUnlinkChannel(unit.id!),
    });
  }

  private executeUnlinkChannel(businessUnitId: number): void {
    const request: UnlinkBusinessUnitRequest = { businessUnitId };

    this.apiBusinessUnitService
      .unlinkBusinessUnit(request, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'configuration.business_unit.dialog.unlink_channel_success',
            ),
          });
          this.loadBusinessUnits();
        },
        error: (error) => {
          console.error(
            '[BusinessUnitComponent] Error unlinking channel:',
            error,
          );
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'configuration.business_unit.dialog.unlink_channel_failed',
            ),
          });
        },
      });
  }

  private subscribeToTreeState(): void {
    this.businessUnitService.tree$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((businessUnits) => {
        this.businessUnits.set(businessUnits);
      });

    this.businessUnitService.loading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoading) => {
        this.treeLoading.set(isLoading);
      });
  }

  loadBusinessUnits(): void {
    this.businessUnitService
      .loadBusinessUnitTree()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private loadUsers(unitId: string, search?: string): void {
    this.usersLoading.set(true);

    this.businessUnitService
      .getUsersByUnitId(unitId, 0, 20, search)
      .pipe(
        finalize(() => {
          this.usersLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => {
        this.users.set(users);
      });
  }

  private findUnitName(
    units: BusinessUnitTreeNode[],
    targetId: string | null,
  ): string {
    if (!targetId) {
      return '';
    }

    for (const unit of units) {
      const unitId = this.getUnitKey(unit);
      if (unitId === targetId) {
        return unit.name || unit.code || '';
      }

      const match = this.findUnitName(this.getChildren(unit), targetId);
      if (match) {
        return match;
      }
    }

    return '';
  }

  private getUnitKey(unit: BusinessUnitTreeNode): string {
    return String(unit.id ?? unit.code ?? unit.name ?? '');
  }

  private getChildren(unit: BusinessUnitTreeNode): BusinessUnitTreeNode[] {
    const rawUnit = unit as Record<string, unknown>;
    const childKeys = ['children', 'subUnits', 'businessUnits'];

    for (const key of childKeys) {
      const children = rawUnit[key];
      if (Array.isArray(children)) {
        return children as BusinessUnitTreeNode[];
      }
    }

    return [];
  }
}
